import React, { useEffect, useState } from "react";
import { Add, Remove } from "@material-ui/icons";
import MOButtonV2 from "../../../components/commons/MOButtonV2";
import { formatCurrency } from "../../../helpers/getSymbolFromCurrency";
import { toastMessage } from "../../../helpers/toastMessage";
import { RATE_TYPES } from "./airportRatesUtils";
import {
  COMMISSION_PRICE_LABEL,
  COMMISSION_PRICE_MULTIPLIER,
  getCommissionPrice,
} from "./rateAnalysisConfig";
import { useDispatch } from "react-redux";
import ThunksRateModule from "../../../redux/actions/thunks/rateModule";

const PRICE_STEP = 1;

const DECIMAL_INPUT_PATTERN = /^\d*\.?\d{0,2}$/;

const CommissionPriceTag = ({ currency, price, className = "" }) => {
  const commissionPrice = getCommissionPrice(price);

  if (commissionPrice === null) {
    return null;
  }

  return (
    <span className={`rate-analysis-commission-price ${className}`.trim()}>
      {COMMISSION_PRICE_LABEL}: {formatCurrency(currency, commissionPrice)}
    </span>
  );
};

const getDistanceRateFormFromMobio = (mobioRate) => ({
  initialRate:
    mobioRate?.initialRate != null ? String(mobioRate.initialRate) : "",
  initialDistance:
    mobioRate?.initialDistance != null
      ? String(mobioRate.initialDistance)
      : "",
  pricePerKM:
    mobioRate?.pricePerKM != null ? String(mobioRate.pricePerKM) : "",
  pricePerMint:
    mobioRate?.pricePerMint != null ? String(mobioRate.pricePerMint) : "",
});

const ServiceLevelPriceUpdate = ({
  offer,
  carType,
  currency,
  tripPrice,
  airportName,
  rateDescription,
  mobioRateValue,
  rateType,
  mobioRate,
  airportId,
  polygonId,
  fromAirport,
}) => {
  const dispatch = useDispatch();
  const isDistanceRate = rateType === RATE_TYPES.DISTANCE;
  const [price, setPrice] = useState("");
  const [distanceForm, setDistanceForm] = useState(
    getDistanceRateFormFromMobio(mobioRate)
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isDistanceRate && tripPrice != null && tripPrice !== "") {
      setPrice(String(tripPrice));
    }
  }, [tripPrice, offer.categoryId, airportName, rateDescription, isDistanceRate]);

  useEffect(() => {
    if (isDistanceRate) {
      setDistanceForm(getDistanceRateFormFromMobio(mobioRate));
    }
  }, [
    isDistanceRate,
    mobioRate,
    offer.categoryId,
    airportName,
    rateType,
  ]);

  const parsedPrice = parseFloat(price);
  const isValidFixedPrice = !Number.isNaN(parsedPrice) && parsedPrice > 0;
  const inputCommissionBase = isValidFixedPrice ? parsedPrice : mobioRateValue;

  const parsedInitialRate = parseFloat(distanceForm.initialRate);
  const parsedInitialDistance = parseFloat(distanceForm.initialDistance);
  const parsedPricePerKM = parseFloat(distanceForm.pricePerKM);
  const parsedPricePerMint = parseFloat(distanceForm.pricePerMint);

  const isValidDistanceForm =
    !Number.isNaN(parsedInitialRate) &&
    parsedInitialRate > 0 &&
    !Number.isNaN(parsedInitialDistance) &&
    parsedInitialDistance > 0 &&
    !Number.isNaN(parsedPricePerKM) &&
    parsedPricePerKM > 0 &&
    distanceForm.pricePerMint !== "" &&
    !Number.isNaN(parsedPricePerMint) &&
    parsedPricePerMint >= 0;

  const adjustPrice = (delta) => {
    const base = Number.isNaN(parsedPrice) ? tripPrice ?? 0 : parsedPrice;
    const next = Math.max(0, parseFloat((base + delta).toFixed(2)));
    setPrice(String(next));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || DECIMAL_INPUT_PATTERN.test(value)) {
      setPrice(value);
    }
  };

  const handleDistanceFieldChange = (field) => (e) => {
    const value = e.target.value;
    if (value === "" || DECIMAL_INPUT_PATTERN.test(value)) {
      setDistanceForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const getUpdateErrorMessage = (response) =>
    response?.response?.data?.msg ||
    response?.message ||
    "Failed to update price.";

  const handleUpdate = async () => {
    if (isDistanceRate) {
      if (!isValidDistanceForm) {
        return;
      }

      const distanceRates = {
        initialRate: parsedInitialRate,
        initialDistance: parsedInitialDistance,
        pricePerKM: parsedPricePerKM,
        pricePerMint: parsedPricePerMint,
      };

      if (mobioRate?.id) {
        distanceRates.id = mobioRate.id;
      } else {
        // distanceRates.serviceLevelCode = carType;
        distanceRates.serviceLevelCode = offer.categoryId
        distanceRates.airportId = airportId;
      }

      if (!mobioRate?.id && (!carType || !airportId)) {
        toastMessage(
          "error",
          "Service level code and airport are required to create a distance rate."
        );
        return;
      }

      setLoading(true);
      try {
        const response = await dispatch(
          ThunksRateModule.updateRateThunk({
            type: "distance",
            distanceRates,
          })
        );

        if (response?.response) {
          toastMessage("error", getUpdateErrorMessage(response));
          return;
        }

        toastMessage(
          "success",
          response?.msg || "Distance rate updated successfully."
        );
      } catch (error) {
        toastMessage("error", error.message || "Failed to update price.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!isValidFixedPrice) {
      return;
    }

    const fixedRates = {
      rate: parsedPrice,
    };

    if (mobioRate?.id) {
      fixedRates.id = mobioRate.id;
    } else {
      fixedRates.serviceLevelCode = offer.categoryId;
      fixedRates.airportId = airportId;
      fixedRates.polygonId = polygonId;
      fixedRates.fromAirport = fromAirport;
    }

    if (
      !mobioRate?.id &&
      (!carType || !airportId || !polygonId)
    ) {
      toastMessage(
        "error",
        "Service level code, airport, and polygon are required to create a fixed rate."
      );
      return;
    }

    setLoading(true);
    try {
      const response = await dispatch(
        ThunksRateModule.updateRateThunk({
          type: "fixed",
          fixedRates,
        })
      );

      if (response?.response) {
        toastMessage("error", getUpdateErrorMessage(response));
        return;
      }

      toastMessage(
        "success",
        response?.msg || "Integration price updated successfully."
      );
    } catch (error) {
      toastMessage("error", error.message || "Failed to update price.");
    } finally {
      setLoading(false);
    }
  };

  const distanceCommissionBase =
    !Number.isNaN(parsedInitialRate) && parsedInitialRate > 0
      ? parsedInitialRate
      : mobioRateValue;

  return (
    <div className="rate-analysis-price-update">
      <div className="rate-analysis-price-update-header">
        <div>
          <p className="rate-analysis-price-update-title">Update rate</p>
          <p className="rate-analysis-price-update-subtitle">
            {airportName ? (
              <>
                Airport: <span>{airportName}</span>
                <span className="rate-analysis-price-update-divider">·</span>
              </>
            ) : null}
            <span>{rateDescription}</span>
            {mobioRateValue !== null && (
              <>
                <span className="rate-analysis-price-update-comma">, </span>
                <CommissionPriceTag currency={currency} price={mobioRateValue} />
              </>
            )}
          </p>
        </div>
        <p className="rate-analysis-price-update-current">
          Trip.com from: {formatCurrency(currency, tripPrice)}
        </p>
      </div>

      <div className="rate-analysis-price-update-controls">
        {isDistanceRate ? (
          <div className="rate-analysis-distance-rate-fields">
            <div className="rate-analysis-price-update-field">
              <label htmlFor={`initial-rate-${offer.offerKey}`}>
                Rate ({currency})
              </label>
              <input
                id={`initial-rate-${offer.offerKey}`}
                type="text"
                inputMode="decimal"
                className="rate-analysis-distance-rate-input"
                value={distanceForm.initialRate}
                onChange={handleDistanceFieldChange("initialRate")}
                disabled={loading}
                placeholder="0.00"
              />
            </div>

            <div className="rate-analysis-price-update-field">
              <label htmlFor={`initial-distance-${offer.offerKey}`}>
                Initial Distance (KM)
              </label>
              <input
                id={`initial-distance-${offer.offerKey}`}
                type="text"
                inputMode="decimal"
                className="rate-analysis-distance-rate-input"
                value={distanceForm.initialDistance}
                onChange={handleDistanceFieldChange("initialDistance")}
                disabled={loading}
                placeholder="0"
              />
            </div>

            <div className="rate-analysis-price-update-field">
              <label htmlFor={`price-per-km-${offer.offerKey}`}>
                Rate (per KM) ({currency})
              </label>
              <input
                id={`price-per-km-${offer.offerKey}`}
                type="text"
                inputMode="decimal"
                className="rate-analysis-distance-rate-input"
                value={distanceForm.pricePerKM}
                onChange={handleDistanceFieldChange("pricePerKM")}
                disabled={loading}
                placeholder="0.00"
              />
            </div>

            <div className="rate-analysis-price-update-field">
              <label htmlFor={`price-per-min-${offer.offerKey}`}>
                Rate (per Min) ({currency})
              </label>
              <input
                id={`price-per-min-${offer.offerKey}`}
                type="text"
                inputMode="decimal"
                className="rate-analysis-distance-rate-input"
                value={distanceForm.pricePerMint}
                onChange={handleDistanceFieldChange("pricePerMint")}
                disabled={loading}
                placeholder="0.00"
              />
            </div>
          </div>
        ) : (
          <div className="rate-analysis-price-update-field">
            <label htmlFor={`price-${offer.offerKey}`}>
              Set integration price ({currency})
            </label>
            <div className="rate-analysis-price-stepper">
              <button
                type="button"
                className="rate-analysis-price-stepper-btn"
                onClick={() => adjustPrice(-PRICE_STEP)}
                disabled={loading}
                aria-label="Decrease price"
              >
                <Remove fontSize="small" />
              </button>
              <input
                id={`price-${offer.offerKey}`}
                type="text"
                inputMode="decimal"
                value={price}
                onChange={handleInputChange}
                disabled={loading}
              />
              <button
                type="button"
                className="rate-analysis-price-stepper-btn"
                onClick={() => adjustPrice(PRICE_STEP)}
                disabled={loading}
                aria-label="Increase price"
              >
                <Add fontSize="small" />
              </button>
            </div>
          </div>
        )}

        <MOButtonV2
          text={loading ? "Updating..." : "Update price"}
          onClick={handleUpdate}
          disabled={
            loading ||
            (isDistanceRate ? !isValidDistanceForm : !isValidFixedPrice)
          }
          loading={loading}
          backgroundColor="#29AEE6"
          textColor="#FFFFFF"
          border="none"
          radius={4}
          height={32}
          fontSize={12}
          padding="6px 16px"
        />
      </div>
      {!isDistanceRate && inputCommissionBase !== null && (
        <p className="rate-analysis-price-update-hint">
          <CommissionPriceTag currency={currency} price={inputCommissionBase} />
          <span className="rate-analysis-commission-multiplier-hint">
            {" "}
            (× {COMMISSION_PRICE_MULTIPLIER})
          </span>
        </p>
      )}
      {isDistanceRate && distanceCommissionBase !== null && (
        <p className="rate-analysis-price-update-hint">
          <CommissionPriceTag currency={currency} price={distanceCommissionBase} />
          <span className="rate-analysis-commission-multiplier-hint">
            {" "}
            (× {COMMISSION_PRICE_MULTIPLIER})
          </span>
        </p>
      )}
    </div>
  );
};

export default ServiceLevelPriceUpdate;
